import apiService, {
  buildFormData,
  getAuthHeader,
} from '@/services/api.service';
import { serialize } from 'object-to-formdata';
import { Vendor } from '@/model/vendor';
import { VendorFormData } from '@/model/vendor-form-data';
import { VendorSettingsPage } from '@/model/vendor-settings-page';
import axios from 'axios';
import i18n from '@/config/i18n';

export default {
  async edit(
    vendorData: Partial<Vendor> | VendorFormData,
    validate: VendorSettingsPage,
  ): Promise<Vendor> {
    return (
      await apiService.put(`vendors/${vendorData.id}`, {
        vendor: vendorData,
        validate,
      })
    ).data.data;
  },
  async getById(vendorId: number): Promise<Vendor> {
    return (await apiService.get(`vendors/${vendorId}`)).data.data;
  },
  async getBySlug(slug: string): Promise<Vendor> {
    return (await apiService.get(`vendors/slug/${slug}`)).data.data;
  },
  /**
   *
   * @param {{name, id}} query
   * @param {string} query.q
   * @param {string} query.id
   * @param {string} query.name
   * @param {string} query.email
   * @returns {Promise<AxiosResponse<any>|undefined>}
   */
  async findAll(query = {}) {
    let queryText = '';
    Object.keys(query).forEach((q) => {
      if (query[q] && query[q] !== '') {
        if (queryText.length > 0 && queryText.slice(-1) !== '&') {
          queryText += '&';
        }
        queryText += q + '=' + encodeURIComponent(query[q]);
      }
    });
    return (await apiService.get('vendors.json?' + queryText)).data.data;
  },
  async create(vendorData: VendorFormData) {
    return (
      await apiService.post(
        'vendors.json',
        serialize(vendorData, {
          nullsAsUndefineds: true,
          booleansAsIntegers: true,
          allowEmptyArrays: true,
          indices: true,
        }),
      )
    ).data.data;
  },
  async userList() {
    return (await apiService.get(`vendors/user-list`)).data.data;
  },
  async getSlackState(vendor: VendorFormData): Promise<string> {
    return (await apiService.get(`vendors/slack-state/${vendor.id}`)).data.data;
  },
  async removeSlackState(vendor: VendorFormData): Promise<string> {
    return (await apiService.get(`vendors/remove-slack-state/${vendor.id}`))
      .data.data;
  },
  async revokingSlack(vendor: VendorFormData): Promise<Vendor> {
    return (await apiService.get(`vendors/slack-revoke/${vendor.id}`)).data
      .data;
  },
  async getRemainingCredits(vendorId: number): Promise<number> {
    return (
      await apiService.get(
        `short-messages/get-remaining-credits.json?vendor_id=${vendorId}`,
      )
    ).data.data.remainingCredits;
  },
  async findTimetablesCount(): Promise<number> {
    return (await apiService.get('timetables/count.json')).data.data;
  },

  async getPictures(
    vendorId: number,
  ): Promise<Array<{ url: string; original: string }>> {
    const response = await apiService.get(`vendors/pictures/${vendorId}`);
    return response.data.data;
  },

  async getLogo(
    vendorId: number,
  ): Promise<Array<{ url: string; original: string }>> {
    const response = await apiService.get(`vendors/logo/${vendorId}`);
    return response.data.data;
  },

  async uploadPictures(
    vendorId: number,
    pictures: File[] | null = null,
    existing: string[] = [],
  ): Promise<{
    success: boolean;
    data: { uploaded_count: number; filenames: string[] };
  }> {
    const formData = buildFormData({ pictures, existing });

    const response = await axios.post(
      `vendors/update-pictures/${vendorId}`,
      formData,
      {
        headers: {
          ...getAuthHeader(),
          'Accept-Language': i18n.global.locale.value,
        },
        transformRequest: [
          (data, headers) => {
            delete headers['Content-Type'];
            return data;
          },
        ],
      },
    );

    return response.data;
  },

  async uploadLogo(
    vendorId: number,
    logo: File | File[] | null = null,
    existing: string[] = [],
  ): Promise<{
    success: boolean;
    data: { filename: string };
  }> {
    const singleLogo = Array.isArray(logo) ? (logo[0] ?? null) : logo;

    const formData = buildFormData({ logo: singleLogo, existing });

    const response = await axios.post(
      `vendors/update-logo/${vendorId}`,
      formData,
      {
        headers: {
          ...getAuthHeader(),
          'Accept-Language': i18n.global.locale.value,
        },
        transformRequest: [
          (data, headers) => {
            delete headers['Content-Type'];
            return data;
          },
        ],
      },
    );

    return response.data;
  },
};
